import { useEffect, useState } from 'react';
import {
  Breakpoints,
  useBreakpoint,
  useTranslation,
  FAIcon,
  Drawer,
  DrawerDirections,
  Theme,
  Shade,
  IconStyle,
} from '@nextgen-web-framework/all';
import { CheckoutStoreDetailsDrawerContent, type ICheckoutAnyType } from '@commerce-checkout-webcomponents/ui';
import ErrorCard, { Errors } from '../ErrorCard/ErrorCard';
import StoreListLayout from './ShopListLayout';
import GoogleMapsLayout from './GoogleMapsLayout';
import styles from './shopfinder.module.scss';
import SearchField from './SearchField';
import { IShopFinderContainerProps, AmwayShopCollections, IShopsOptions } from './types/propsTypes';

const ShopFinderContainer = ({ shopLocations, isLocationAccessAllowed }: IShopFinderContainerProps) => {
  const t = useTranslation();
  const config = ConfigService.getConfigs();
  const ASSETS_BASE_PATH = config.NEXT_ASSETS_BASE_PATH;
  const viewPortType = useBreakpoint() as Breakpoints;
  const isDesktop = [Breakpoints.LG, Breakpoints.XL, Breakpoints.MD].includes(viewPortType);

  const [selectedShop, setSelectedShop] = useState<string>('');
  const [shopDetailsData, setShopDetailsData] = useState<IShopsOptions | null>(null);
  const [displayedShopLocations, setDisplayedShopLocations] = useState<IShopsOptions[]>([]);
  const [isShopDetailVisible, setIsShopDetailVisible] = useState<boolean>(false);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const [searchShopLocations, setSearchShopLocations] = useState<IShopsOptions[]>();
  const [searchText, setSearchText] = useState('');
  const [isShowFullScreenMap, setIsShowFullScreenMap] = useState(false);

  const shopProps = {
    setSelectedShop,
    shopLocations: displayedShopLocations,
    selectedShop,
  };

  useEffect(() => {
    const finalArray = isSearch ? searchShopLocations : shopLocations;
    setDisplayedShopLocations(finalArray ?? []);
  }, [shopLocations, searchShopLocations, isSearch]);

  useEffect(() => {
    if (!selectedShop && shopLocations && shopLocations.length > 0) {
      setSelectedShop(shopLocations[0].id);
    }
  }, [shopLocations, selectedShop]);

  const handleShowShopDetails = (shopDetailData: IShopsOptions) => {
    setShopDetailsData(shopDetailData);
    setIsShopDetailVisible(true);
  };

  const renderShopDetails = () => (
    <div className={styles['shop-details-container']}>
      <div className={styles['shop-details-header']}>
        <span
          data-testid="back-to-shop-list"
          className={styles['shop-details-backToShops']}
          role="button"
          tabIndex={0}
          aria-labelledby="back-to-shop-text"
          onClick={() => setIsShopDetailVisible(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              setIsShopDetailVisible(false);
            }
          }}
        >
          <FAIcon iconName={'angle-left'} className={styles['shop-detail-back-icon']} />
          <span id="back-to-shop-text" className={styles['shop-detail-back-text']}>
            {t('web.shopFinder.backToShopList')}
          </span>
        </span>
      </div>
      <CheckoutStoreDetailsDrawerContent
        imgSrc={`${ASSETS_BASE_PATH}/images/AmwayTabLogo.png`}
        imgAlt={'Amway Logo'}
        storeDetailsData={shopDetailsData as ICheckoutAnyType}
      />
    </div>
  );
  const renderGoogleMapFullScreen = () => (
    <Drawer
      header={
        <div className={styles['drawer-title']}>
          <FAIcon
            theme={Theme.BLACK}
            iconName={'chevron-left'}
            shade={Shade.DARK}
            iconStyle={IconStyle.REGULAR}
            className={styles['backLink-icon']}
            onClick={() => setIsShowFullScreenMap(false)}
          />
          <span className={styles['shop-finder-title']}>{t('Shop Finder')}</span>
        </div>
      }
      className={styles['google-layout-drawer-container']}
      closeIcon={false}
      bottomGap={0}
      useReactPortal={true}
      portalContainerId={'react-portal-drawer'}
      direction={DrawerDirections.FULLHEIGHT}
    >
      {renderGoogleMapLayout()}
    </Drawer>
  );

  const renderGoogleMapLayout = () =>
    displayedShopLocations?.length ? (
      <GoogleMapsLayout {...shopProps} shopLocations={shopLocations} setShowFullScreenMap={setIsShowFullScreenMap} />
    ) : (
      <div className={styles['no-shop-card-container']}>
        <ErrorCard
          className={styles['shop-finder-no-stores-card']}
          title={t('web.shopFinder.noStoresFound')}
          imageUrl={`${ASSETS_BASE_PATH}${'/images/checkoutNoStoreError.svg'}`}
          type={Errors.General}
          customImageClass={styles['checkout-no-stores-card-image']}
          customCtaComponent={<></>}
        />
      </div>
    );

  const filterShopBySearchText = (
    shopLoactions: Array<IShopsOptions> | undefined,
    keys: Array<string>,
    search: string,
  ) => {
    const lowSearch = search.toLowerCase();
    return shopLoactions?.filter((shops) =>
      keys.some((key) =>
        String(shops[key as keyof IShopsOptions])
          .toLowerCase()
          .includes(lowSearch),
      ),
    );
  };

  const storeFieldForSearch = ['addressLine', 'name', 'id'];

  const handleSearchShop = (searchText: string) => {
    setSearchText(searchText);
    if (searchText.length >= 3) {
      setIsSearch(true);
      const searchedShops = filterShopBySearchText(shopLocations, storeFieldForSearch, searchText);
      setSearchShopLocations(searchedShops);
    } else {
      setIsSearch(false);
    }
  };

  const handleSearchClearButton = () => {
    setSearchText('');
    setIsSearch(false);
  };

  return (
    <>
      {isShowFullScreenMap && !isDesktop ? (
        renderGoogleMapFullScreen()
      ) : isDesktop ? (
        <>
          <div className={styles['shop-finder-shop-list']} data-testid="shop-finder-test">
            {isShopDetailVisible ? (
              renderShopDetails()
            ) : (
              <>
                <SearchField
                  handleSearchShop={handleSearchShop}
                  searchText={searchText}
                  handleSearchClearButton={handleSearchClearButton}
                />
                <StoreListLayout
                  {...shopProps}
                  isLocationAccessAllowed={isLocationAccessAllowed}
                  availableShops={displayedShopLocations?.length}
                  shopDetails={(data) => handleShowShopDetails(data)}
                />
              </>
            )}
          </div>
          <div className={styles['shop-finder-map']}>{renderGoogleMapLayout()}</div>
        </>
      ) : (
        <>
          {isShopDetailVisible ? (
            renderShopDetails()
          ) : (
            <>
              <div data-testid="shop-finder-test">
                <SearchField
                  handleSearchShop={handleSearchShop}
                  searchText={searchText}
                  handleSearchClearButton={handleSearchClearButton}
                />
              </div>
              {displayedShopLocations?.length ? (
                <div className={styles['shop-finder-shop-list']}>
                  <StoreListLayout
                    {...shopProps}
                    isLocationAccessAllowed={isLocationAccessAllowed}
                    availableShops={displayedShopLocations?.length}
                    shopDetails={(data) => handleShowShopDetails(data)}
                    setShowFullScreenMap={setIsShowFullScreenMap}
                  />
                </div>
              ) : null}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ShopFinderContainer;
export type { AmwayShopCollections, IShopsOptions };
